import { useMemo } from 'react';
import { Pencil, Trash2, Clock, AlertCircle, History } from 'lucide-react';
import type { SensorRule } from '../../services/sensorRules';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

interface Props {
  rules: SensorRule[];
  onEdit: (rule: SensorRule) => void;
  onDelete: (rule: SensorRule) => void;
  onToggleEnabled: (rule: SensorRule, next: boolean) => void;
  onShowHistory: (rule: SensorRule) => void;
}

export default function SensorRuleList({ rules, onEdit, onDelete, onToggleEnabled, onShowHistory }: Props) {
  const orgTz = useOrgTimezone();

  const summaries = useMemo(() => rules.map(r => ({
    rule: r,
    summary: summariseRule(r),
  })), [rules]);

  if (rules.length === 0) {
    return <div className="text-sm text-gray-400 italic">No rules for this channel yet.</div>;
  }

  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden bg-white">
      {summaries.map(({ rule, summary }) => (
        <li key={rule.id} className={`p-3 ${rule.enabled ? '' : 'opacity-60'}`}>
          <div className="flex items-start gap-3">
            <label className="pt-0.5">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => onToggleEnabled(rule, e.target.checked)}
                title={rule.enabled ? 'Disable' : 'Enable'}
              />
            </label>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-medium text-sm truncate">{rule.name}</div>
                {rule.last_state && (
                  <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                    ARMED
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-600 mt-1">{summary}</div>
              <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Last fired: {rule.last_fired_at ? formatDateTimeInTz(rule.last_fired_at, orgTz) : 'never'}
                </span>
                {rule.last_error && (
                  <span className="inline-flex items-center gap-1 text-red-600" title={rule.last_error}>
                    <AlertCircle className="w-3 h-3" /> last run had errors
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onShowHistory(rule)}
                title="Event history"
                className="p-1.5 text-gray-500 hover:text-blue-600"
              >
                <History className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onEdit(rule)}
                title="Edit"
                className="p-1.5 text-gray-500 hover:text-blue-600"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(rule)}
                title="Delete"
                className="p-1.5 text-gray-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function summariseRule(r: SensorRule): string {
  const condText = summariseCondition(r.condition);
  const actionText = r.actions.length === 0
    ? 'no actions'
    : r.actions.map(a => a.type.replace(/_/g, ' ')).join(', ');
  return `If ${condText} → ${actionText}`;
}

function summariseCondition(c: SensorRule['condition']): string {
  if (!c || typeof c !== 'object') return '—';
  if ('all' in c || 'any' in c) {
    const list = 'all' in c ? c.all : c.any;
    const joiner = 'all' in c ? ' AND ' : ' OR ';
    return list.map(summariseCondition).join(joiner);
  }
  const opMap: Record<string, string> = { lt: '<', lte: '≤', gt: '>', gte: '≥', eq: '=', neq: '≠' };
  return `${c.metric} ${opMap[c.op] || c.op} ${c.value}`;
}
